import { observable, makeObservable, action, computed } from 'mobx';
import { TrackingHelper, PiwikArticleTrackingData, KrakenArticleTrackingData } from '@escid-espp1/helpers';
import { ILocalization, ITakeAwayArticleTrackingData } from '../types';
import DataStore from './DataStore';
import OrderModuleStore from './OrderModuleStore';
import { MixedMediaStore } from './MixedMediaStore';
import { VariantStore } from './VariantStore';
import {
    IResponseData,
    IVariantSelection,
    IBookmarkArticleDataResponse,
    ArticleDataInjectionRequest,
    ViewType,
} from './types';
import Fetcher from './Fetcher';
import { ScrollLock } from '../helper/scrollLock';
import { showCaseContainerId } from '../components/orderModal/mediaFrame/mediaFrame';
import { isMobileOnly } from '../helper/html-helper';

const bookmarkIntegrationSignature = 'ESCID.ESPP.ProductDetailsPage.Bookmark.OrderModal';
const bookmarkRequestEventName = 'ESCID.ESPP.Bookmark.RequestArticleDataInjection';
const bookmarkResponseEventName = 'ESCID.ESPP.Bookmark.ArticleDataInjection';

export class MainStore {
    public dataStore: DataStore;
    public orderModuleStore: OrderModuleStore;
    public mixedMediaStore: MixedMediaStore;
    public variantStore: VariantStore;
    public readonly scrollLock = new ScrollLock();
    public localization: ILocalization;
    public fetcher: Fetcher;
    public readonly piwikProEnableEcommerceTracking: boolean;
    public readonly takeAwayTrackingData?: ITakeAwayArticleTrackingData; 
    public readonly enableMetaPixel: boolean;
    public readonly enableTikTokPixel: boolean;
    public readonly enableTabNavigation: boolean;
    public readonly enabledClientLogging: boolean;
    public readonly enablePictureInPicture: boolean;
    public readonly enableKraken: boolean;
    public readonly disableDirectEcommerceTracking: boolean;
    public readonly esBrand = 'engelbert strauss';
    public trackingHelper: TrackingHelper;
    public tabNavTargetOnClose: Element;

    // @observable:
    public fetchedData: IResponseData;
    public cdnAssetsUrl: string;
    public play3d: boolean;
    public isMobileShop: boolean;
    public isEsApp: boolean;
    public isGlobal: boolean;
    public isSidepanel: boolean;
    public initialScrollPosition: number;

    public checkSelectedSizeType(variantSelection: IVariantSelection) {
        const isFirstSelectedTypeSoldOut =
            variantSelection.availableSizeTypes.find(sizeType => {
                if (sizeType.sizeTypeName == variantSelection.selectedSizeType)
                    return sizeType.sizes.every(size => {
                        return size.salesArticleVariantKey == '';
                    });
            }) != null;

        if (isFirstSelectedTypeSoldOut) {
            const nonSoldOutSizeType = variantSelection.availableSizeTypes.find(sizeType => {
                return !sizeType.sizes.every(size => {
                    return size.salesArticleVariantKey == '';
                });
            });
            if (nonSoldOutSizeType) {
                variantSelection.selectedSizeType = nonSoldOutSizeType.sizeTypeName;
                variantSelection.selectedSize = nonSoldOutSizeType.sizes.find(size => {
                    return size.salesArticleVariantKey;
                })?.size;
            }
        }
    }

    constructor(initialData: IResponseData, localization: ILocalization, itemOrigin: string, magicLink: string,
        enabledClientLogging?: boolean, initialScrollPosition?: number, contentContainerId?: string,
        viewType?: ViewType, takeAwayTrackingData?: ITakeAwayArticleTrackingData) {
        this.dataStore = new DataStore(initialData, this);

        this.isMobileShop = initialData.contextData.isMobile;
        this.isEsApp = initialData.contextData.isEsApp;
        this.isGlobal = initialData.contextData.portal.includes('.GLOBAL.');
        this.isSidepanel = viewType === 'sidepanel';
        this.fetchedData = initialData;
        this.mixedMediaStore = new MixedMediaStore(this);
        this.initialScrollPosition = typeof initialScrollPosition === 'number' ? initialScrollPosition : 0;

        // If all sizes in selected size type are sold out
        // change first selected size type and first selected size
        this.checkSelectedSizeType(initialData.variantSelection);
        this.checkSelectedSizeType(this.dataStore.variantSelection);

        this.takeAwayTrackingData = takeAwayTrackingData;
        this.variantStore = new VariantStore(this);
        this.fetcher = new Fetcher(magicLink, enabledClientLogging, this.isMobileShop);
        this.orderModuleStore = new OrderModuleStore(this, itemOrigin, contentContainerId);

        this.cdnAssetsUrl = initialData.contextData.baseUrlForAssets;

        this.localization = localization;
        this.play3d = false;

        this.piwikProEnableEcommerceTracking = initialData.contextData.piwikProEnableEcommerceTracking;
        this.enableMetaPixel = initialData.contextData.enableMetaPixel;
        this.enableTikTokPixel = initialData.contextData.enableTikTokPixel;
        this.enableTabNavigation = initialData.contextData.enableTabNavigation;
        this.enabledClientLogging = initialData.contextData.enabledClientLogging;
        this.enablePictureInPicture = initialData.contextData.enablePictureInPicture;
        this.enableKraken = initialData.contextData.enableKraken;
        this.disableDirectEcommerceTracking = initialData.contextData.disableDirectEcommerceTracking;
        this.trackingHelper = new TrackingHelper(this.piwikProEnableEcommerceTracking,
            this.enableMetaPixel, this.enableTikTokPixel, this.enableKraken, this.enabledClientLogging, itemOrigin, null);

        makeObservable(this, {
            fetchedData: observable,
            cdnAssetsUrl: observable,
            play3d: observable,
            variantChanged: action,
            setFetchedData: action,
            togglePlay3D: action,
            getIsMobileView: computed,
        });

        this.trackVisitedArticle(initialData, takeAwayTrackingData);

        if (typeof window !== 'undefined' && window.shell) {
            window.shell.subscribeTo(
                bookmarkRequestEventName,
                (request: ArticleDataInjectionRequest) => {
                    const response: IBookmarkArticleDataResponse = {
                        ...this.fetchedData.bookmark,
                        requester: request.requester,
                        grossPrice: this.fetchedData.orderModule.articlePrice.basicPrice.grossValue,
                        navKeyPath: this.fetchedData.breadcrumb.category.navigationKeyPath,
                        currencyISOCode: this.fetchedData.orderModule.articlePrice.currency.isoCode,
                    };
                    /*
                    Condition needed for https://jira.ztna.cid.com/browse/ESPP-9930
                    to prevent that color change in order modal from Mix&Match shutter 
                    cause Shortlist modal color change. 
                    */
                    if (!request.requester || request.requester == null)
                        window.shell.publishTo(bookmarkResponseEventName, response);
                },
                bookmarkIntegrationSignature
            );
        }

        this.setGroupElement = this.setGroupElement.bind(this);
        this.setModalElement = this.setModalElement.bind(this);
        this.setFocusableElement = this.setFocusableElement.bind(this);
        this.setTabNavFocus = this.setTabNavFocus.bind(this);
        this.tabNavArrowListener = this.tabNavArrowListener.bind(this);
        this.setArrowKeyTabNavListener = this.setArrowKeyTabNavListener.bind(this);
        this.removeArrowKeyTabNavListener = this.removeArrowKeyTabNavListener.bind(this);
    }

    trackVisitedArticle(data: IResponseData, takeAwayTrackingData?: ITakeAwayArticleTrackingData) {
        const trackingData: PiwikArticleTrackingData = {
            id: data.orderModule.articleInfo.salesArticleVariantKey,
            name: data.orderModule.articleInfo.title,
            price: data.orderModule.articlePrice.basicPrice,
            brand: data.orderModule.articleInfo.brand,
            category: data.breadcrumb.category.navigationKeyPath,
            currencyCode: data.orderModule.articlePrice.currency.isoCode,
            variantId: data.orderModule.articleInfo.salesArticleVariantKey,
            colorCode: data.variantSelection.selectedColor?.code.toString(),
            colorName: data.variantSelection.selectedColor?.name,
            sizeCode: data.variantSelection.selectedSize?.code.toString(),
            sizeName: data.variantSelection.selectedSize?.name,
            modelCode: data.variantSelection.selectedModel?.code.toString(),
            modelName: data.variantSelection.selectedModel?.label,
            masterArticleNo: data.orderModule.articleInfo.masterArticleNo.toString(),
            productRecommendationFragment: takeAwayTrackingData?.productRecommendationFragment,
            productRecommendationModel: takeAwayTrackingData?.productRecommendationModel,
            productRecommendationTileIndex: takeAwayTrackingData?.productRecommendationTileIndex,
        };
    
        if(this.enableKraken) {
            const krakenTrackingData: KrakenArticleTrackingData = {
                ...trackingData,
                id: data.orderModule.articleInfo.salesArticleNo,
            }
            this.trackingHelper.krakenTrackProductViewed(krakenTrackingData, 'ESPP3.OrderModal', 'MODAL_ORDER/');
        }

        if(!this.disableDirectEcommerceTracking) {
            const piwikTrackingData = { ...trackingData };
            piwikTrackingData.id = this.dataStore.orderModule.articleInfo.salesArticleNo;

            this.trackingHelper.piwikTrackVisitedArticle(piwikTrackingData, this.orderModuleStore.articleCount || 0,
                'MODAL_ORDER/');
            this.trackingHelper.metaTrackVisitedArticle(trackingData);
            this.trackingHelper.tiktokTrackVisitedArticle(trackingData);
        } 
    }

    setGroupElement(element: Element | null, order?: number) {
        if (this.enableTabNavigation && element && !element.hasAttribute('group')) {
            element.toggleAttribute('group');

            if(typeof order === 'number') {
                element.setAttribute('order', String(order));
            }
        }
    }

    setModalElement(element: Element) {
        if (this.enableTabNavigation && element && !element.hasAttribute('modal')) {
            element.toggleAttribute('modal');
        }
    }

    setFocusableElement(element: Element | null, order?: number) {
        if (this.enableTabNavigation && element && !element.hasAttribute('focusable')) {
            element.toggleAttribute('focusable');

            if(typeof order === 'number') {
                element.setAttribute('order', String(order));
            }
        }
    }

    setTabNavFocus(element: Element) {
        if (this.enableTabNavigation && typeof window !== 'undefined' && window.shell && window.shell.tabNav) {
            window.shell.tabNav.focus(element);
        }
    }

    tabNavArrowListener(event) {
        if (this.enableTabNavigation && typeof window !== 'undefined' && window.shell && window.shell.tabNav) {
            if (typeof window !== 'undefined' && window.shell && window.shell.tabNav) {
                if (event.key === 'ArrowLeft') {
                    window.shell.tabNav.focusPrevious();
                } else if (event.key === 'ArrowRight') {
                    window.shell.tabNav.focusNext();
                }
            }
        }
    }

    setArrowKeyTabNavListener(element: Element) {
        if (this.enableTabNavigation && typeof window !== 'undefined' && window.shell && window.shell.tabNav) {
            element.addEventListener('keydown', this.tabNavArrowListener);
        }
    }

    removeArrowKeyTabNavListener(element: Element) {
        if (this.enableTabNavigation && typeof window !== 'undefined' && window.shell && window.shell.tabNav) {
            element.removeEventListener('keydown', this.tabNavArrowListener);
        }
    }

    // action
    variantChanged(fetchedData: IResponseData) {
        this.setFetchedData(fetchedData);
        this.trackVisitedArticle(fetchedData, this.takeAwayTrackingData);
        this.dataStore.updateData(fetchedData);
        this.mixedMediaStore.dataUpdate(fetchedData.mixedMedia);

        window.shell.publishTo('ESPP.OrderModal.Update', {
            showCaseContainerId,
            response: fetchedData,
            restartAnimation: this.play3d,
        });
    }

    setFetchedData(newFetchedData: IResponseData) {
        this.fetchedData = newFetchedData;
    }

    togglePlay3D() {
        this.play3d = !this.play3d;
        window.shell.publishTo('SCS.PlayPauseAnimation',
            { containerId: showCaseContainerId, play3d: this.play3d });
    }

    // Computed
    get getIsMobileView(): boolean {
        return isMobileOnly || this.isEsApp || this.isSidepanel;
    }
}
